import unionBy from "lodash/unionBy";

import { ASSET_TYPE, PRINT_TYPE } from "./rrdConstants";
import {
  RRD_EXPIRY_MODE,
  RRD_LAYOUT_KEY,
  RRD_LAYOUTS,
  RRD_LEGACY_PRINT_TYPE_LAYOUT,
} from "./rrdLayoutConfig";

type RRDAsset = Record<string, any>;
type RRDLocation = Record<string, any>;
type RRDSelectionValues = Record<string, any>;
type RRDAssetBucketName =
  | "heroImages"
  | "largeHeroImages"
  | "mediumHeroImages"
  | "fixedImages"
  | "coupons";
type RRDAssetGroups = Record<RRDAssetBucketName, RRDAsset[]> & {
  byType: Record<string, RRDAsset[]>;
};

const ASSET_TYPE_ALIAS: Record<string, string> = {
  hero: ASSET_TYPE.HERO,
  hero_image: ASSET_TYPE.HERO,
  large_hero: ASSET_TYPE.LARGE_HERO,
  large_hero_image: ASSET_TYPE.LARGE_HERO,
  medium_hero: ASSET_TYPE.MEDIUM_HERO,
  medium_hero_image: ASSET_TYPE.MEDIUM_HERO,
  fixed: ASSET_TYPE.FIXED_IMAGE,
  fixed_image: ASSET_TYPE.FIXED_IMAGE,
  coupon: ASSET_TYPE.COUPON,
};

const ASSET_BUCKET_BY_TYPE: Record<string, RRDAssetBucketName> = {
  [ASSET_TYPE.HERO]: "heroImages",
  [ASSET_TYPE.LARGE_HERO]: "largeHeroImages",
  [ASSET_TYPE.MEDIUM_HERO]: "mediumHeroImages",
  [ASSET_TYPE.FIXED_IMAGE]: "fixedImages",
  [ASSET_TYPE.COUPON]: "coupons",
};

export { RRD_EXPIRY_MODE, RRD_LAYOUT_KEY };

export function normalizeRRDAssetType(assetType?: string | null) {
  if (!assetType) return "";
  return ASSET_TYPE_ALIAS[String(assetType).trim().toLowerCase()] || assetType;
}

export function getRRDLayoutSpec(layoutKey?: string | null) {
  if (!layoutKey) return null;
  return (RRD_LAYOUTS as Record<string, any>)[layoutKey] || null;
}

export function getRRDLayoutKeyFromLegacyPrintType(printType?: string | null) {
  if (!printType) return null;
  return (
    (RRD_LEGACY_PRINT_TYPE_LAYOUT as Record<string, string>)[printType] || null
  );
}

export function getRRDLayoutSpecForPrintType(printType?: string | null) {
  return getRRDLayoutSpec(getRRDLayoutKeyFromLegacyPrintType(printType));
}

export function getRRDRequiredAssetCounts(layoutKey?: string | null) {
  return getRRDLayoutSpec(layoutKey)?.requiredAssets || {};
}

export function getRRDCompletionAssetCounts(layoutKey?: string | null) {
  return getRRDLayoutSpec(layoutKey)?.completionAssets || {};
}

export function getRRDSelectableAssetTypes(layoutKey?: string | null) {
  return getRRDLayoutSpec(layoutKey)?.selectableAssetTypes || [];
}

export function getRRDLayoutSlots(layoutKey?: string | null) {
  return getRRDLayoutSpec(layoutKey)?.slots || [];
}

export function isRRDFixedImageRequired(layoutKey?: string | null) {
  return !!getRRDLayoutSpec(layoutKey)?.fixedImageRequired;
}

export function supportsRRDDifferentExpiry(layoutKey?: string | null) {
  return getRRDLayoutSpec(layoutKey)?.expiryMode === RRD_EXPIRY_MODE.FRONT_BACK;
}

export function normalizeRRDPrintTypeContext(source: Record<string, any> = {}) {
  const layoutKey =
    source.layoutKey ||
    source.layout_key ||
    source.printTypeLayoutKey ||
    source.print_type_layout_key ||
    getRRDLayoutKeyFromLegacyPrintType(source.printType || source.print_type);

  const layoutSpec = getRRDLayoutSpec(layoutKey);
  const printType =
    source.printType ||
    source.print_type ||
    layoutSpec?.legacyPrintType ||
    layoutSpec?.printTypeLabel ||
    "";

  const printTypeLabel =
    source.printTypeLabel ||
    source.print_type_label ||
    source.print_type_display ||
    layoutSpec?.printTypeLabel ||
    (printType === PRINT_TYPE.VALUE.value
      ? PRINT_TYPE.VALUE.label
      : printType === PRINT_TYPE.POSTCARD.value
        ? PRINT_TYPE.POSTCARD.label
        : printType);

  return {
    printType,
    printTypeId: source.printTypeId || source.print_type_id || null,
    printTypeLabel,
    layoutKey: layoutSpec?.layoutKey || layoutKey || null,
    layoutSpec,
  };
}

export function doRRDPrintTypesMatch(
  leftPrintType?: string | null,
  rightPrintType?: string | null
) {
  if (!leftPrintType || !rightPrintType) return true;
  if (leftPrintType === rightPrintType) return true;

  return (
    normalizeRRDPrintTypeContext({ print_type: leftPrintType })
      .printTypeLabel ===
    normalizeRRDPrintTypeContext({ print_type: rightPrintType }).printTypeLabel
  );
}

function asArray<T = any>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function firstArray<T = any>(...values: unknown[]): T[] {
  return (
    (values.find((value) => Array.isArray(value)) as T[] | undefined) || []
  );
}

function normalizeAsset(asset: RRDAsset, fallbackAssetType: string) {
  const assetType = normalizeRRDAssetType(
    asset?.asset_type || asset?.assetType || fallbackAssetType
  );
  return {
    ...asset,
    asset_type: assetType,
    assetType,
  };
}

function addAssets(
  groups: RRDAssetGroups,
  assets: unknown,
  fallbackAssetType: string
) {
  for (const asset of asArray(assets)) {
    const normalizedAsset = normalizeAsset(asset, fallbackAssetType);
    const bucketName = ASSET_BUCKET_BY_TYPE[normalizedAsset.asset_type];
    if (bucketName) {
      groups[bucketName].push(normalizedAsset);
      groups.byType[normalizedAsset.asset_type].push(normalizedAsset);
    }
  }
}

function getLegacyAssetGroup(location: RRDLocation, legacyKey: string) {
  return firstArray(location?.assets?.[legacyKey])?.[0] || {};
}

export function normalizeRRDLocationAssets(location: RRDLocation = {}) {
  const { layoutKey } = normalizeRRDPrintTypeContext(location);
  const groups = {
    heroImages: [],
    largeHeroImages: [],
    mediumHeroImages: [],
    fixedImages: [],
    coupons: [],
    byType: {
      [ASSET_TYPE.HERO]: [],
      [ASSET_TYPE.LARGE_HERO]: [],
      [ASSET_TYPE.MEDIUM_HERO]: [],
      [ASSET_TYPE.FIXED_IMAGE]: [],
      [ASSET_TYPE.COUPON]: [],
    },
  };

  const valueAssets = getLegacyAssetGroup(location, "value");
  const postcardAssets = getLegacyAssetGroup(location, "postcard");
  const megaAssets = getLegacyAssetGroup(location, "mega_sheet");
  const dynamicAssets = getLegacyAssetGroup(location, "dynamic_postcard");
  const shouldReadValueAssets =
    !layoutKey || layoutKey === RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C;
  const shouldReadMegaAssets =
    !layoutKey || layoutKey === RRD_LAYOUT_KEY.MEGA_SHEET_1L_1M_8C;
  const shouldReadDynamicAssets =
    !layoutKey || layoutKey === RRD_LAYOUT_KEY.DYNAMIC_POSTCARD_1H_6C_FIXED;
  const shouldReadPostcardAssets =
    !layoutKey ||
    [
      RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C,
      RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_4C_FIXED,
      RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_3C_FIXED,
    ].includes(layoutKey);

  addAssets(
    groups,
    firstArray(
      location.heroImages,
      location.hero_images,
      location.assets?.heroImages,
      location.assets?.hero_images
    ),
    ASSET_TYPE.HERO
  );
  if (shouldReadValueAssets) {
    addAssets(groups, valueAssets.hero_images, ASSET_TYPE.HERO);
  }
  if (shouldReadPostcardAssets) {
    addAssets(groups, postcardAssets.hero_images, ASSET_TYPE.HERO);
  }
  if (shouldReadDynamicAssets) {
    addAssets(groups, dynamicAssets.hero_images, ASSET_TYPE.HERO);
  }

  addAssets(
    groups,
    firstArray(
      location.largeHeroImages,
      location.large_hero_images,
      location.assets?.largeHeroImages,
      location.assets?.large_hero_images,
      shouldReadMegaAssets ? megaAssets.large_hero_images : []
    ),
    ASSET_TYPE.LARGE_HERO
  );
  addAssets(
    groups,
    firstArray(
      location.mediumHeroImages,
      location.medium_hero_images,
      location.assets?.mediumHeroImages,
      location.assets?.medium_hero_images,
      shouldReadMegaAssets ? megaAssets.medium_hero_images : []
    ),
    ASSET_TYPE.MEDIUM_HERO
  );
  addAssets(
    groups,
    firstArray(
      location.fixedImages,
      location.fixed_images,
      location.assets?.fixedImages,
      location.assets?.fixed_images,
      postcardAssets.fixed_images,
      dynamicAssets.fixed_images
    ),
    ASSET_TYPE.FIXED_IMAGE
  );
  addAssets(
    groups,
    [location.fixed_image].filter(Boolean),
    ASSET_TYPE.FIXED_IMAGE
  );
  if (shouldReadPostcardAssets) {
    addAssets(groups, postcardAssets.fixed_images, ASSET_TYPE.FIXED_IMAGE);
  }
  if (shouldReadDynamicAssets) {
    addAssets(groups, dynamicAssets.fixed_images, ASSET_TYPE.FIXED_IMAGE);
  }
  addAssets(
    groups,
    firstArray(location.coupons, location.assets?.coupons),
    ASSET_TYPE.COUPON
  );
  if (shouldReadValueAssets) {
    addAssets(groups, valueAssets.coupons, ASSET_TYPE.COUPON);
  }
  if (shouldReadPostcardAssets) {
    addAssets(groups, postcardAssets.coupons, ASSET_TYPE.COUPON);
  }
  if (shouldReadDynamicAssets) {
    addAssets(groups, dynamicAssets.coupons, ASSET_TYPE.COUPON);
  }
  if (shouldReadMegaAssets) {
    addAssets(groups, megaAssets.coupons, ASSET_TYPE.COUPON);
  }

  for (const bucketName of [
    "heroImages",
    "largeHeroImages",
    "mediumHeroImages",
    "fixedImages",
    "coupons",
  ] as RRDAssetBucketName[]) {
    groups[bucketName] = unionBy(groups[bucketName], "id");
  }

  for (const assetType of Object.keys(groups.byType)) {
    groups.byType[assetType] = unionBy(groups.byType[assetType], "id");
  }

  return groups;
}

export function getRRDAssetCountFromSelection(
  values: RRDSelectionValues = {},
  assetType?: string | null
) {
  const normalizedType = normalizeRRDAssetType(assetType);
  if (normalizedType === ASSET_TYPE.COUPON)
    return asArray(values.coupons).length;

  const directBucket =
    normalizedType === ASSET_TYPE.LARGE_HERO
      ? values.large_hero_images || values.largeHeroImages
      : normalizedType === ASSET_TYPE.MEDIUM_HERO
        ? values.medium_hero_images || values.mediumHeroImages
        : values.hero_images || values.heroImages;

  const directCount = asArray(directBucket).filter((asset) => {
    const selectedType = normalizeRRDAssetType(
      asset?.asset_type || asset?.assetType || normalizedType
    );
    return selectedType === normalizedType;
  }).length;

  if (normalizedType === ASSET_TYPE.HERO) return directCount;

  const sharedHeroCount = asArray(
    values.hero_images || values.heroImages
  ).filter(
    (asset) =>
      normalizeRRDAssetType(asset?.asset_type || asset?.assetType) ===
      normalizedType
  ).length;

  return directCount || sharedHeroCount;
}
